import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Ban, Bot as BotIcon, ChevronLeft, ChevronRight, File as FileIcon, Image, Mic, Pencil, Receipt, Send, Unlock, User, Video,
} from "lucide-react";
import { dashboardService } from "../../services/api";
import { fmtDateTimezone } from "../utils/dateUtils/fmtDate";
import StatusPill from "../../components/StatusPill/StatusPill";
import TelegramThumb from "../Bots/Flows/components/FlowNode/TelegramThumb";
import { useNotification } from "../../components/Notifications/NotificationContext";
import useUrlFilters from "./useUrlFilters";
import FilterChip from "./FilterChip";
import AdminFilters from "./AdminFilters";

const MEDIA_ICONS = { photo: Image, video: Video, voice: Mic };

const fmtBRL = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

function BotMediaThumb({ media, botId }) {
    // Vídeos têm thumbnail_file_id; voice não tem preview visual
    const fileId = media.thumbnail_file_id || (media.media_type === "photo" ? media.file_id : null);
    if (!fileId) {
        const Icon = MEDIA_ICONS[media.media_type] || FileIcon;
        return (
            <div className="adm-media-placeholder" title={media.media_type}>
                <Icon size={16} />
            </div>
        );
    }
    return <TelegramThumb fileId={fileId} botId={botId} imgClassName="" />;
}

function BotCopyPreview({ text }) {
    const [expanded, setExpanded] = useState(false);
    if (!text?.trim()) return <div className="adm-copy adm-copy--empty">Sem copy configurada</div>;
    const isLong = text.length > 160 || text.split("\n").length > 2;
    return (
        <div className="adm-copy-wrap">
            <div className={`adm-copy ${expanded ? "" : "is-clamped"}`}>{text}</div>
            {isLong && (
                <button type="button" className="adm-copy-toggle" onClick={() => setExpanded(!expanded)}>
                    {expanded ? "ver menos" : "ver mais"}
                </button>
            )}
        </div>
    );
}

function BotStatus({ bot }) {
    if (bot.is_blocked) return <StatusPill color="red">Bloqueado</StatusPill>;
    if (bot.is_running) return <StatusPill color="green" pulse>Rodando</StatusPill>;
    return <StatusPill color="gray">Parado</StatusPill>;
}

function BotRow({ bot, onOwner, onEdit, onDisparos, onSales, onBlock, onUnblock }) {
    const title = bot.bot_username ? `@${bot.bot_username}` : (bot.bot_name || `Bot #${bot.id}`);
    const plans = bot.plans || [];
    const media = bot.media || [];
    return (
        <article className="adm-row">
            <div className="adm-ident">
                <div className="adm-avatar adm-avatar--square">
                    {bot.bot_photo_file_id
                        ? <TelegramThumb fileId={bot.bot_photo_file_id} botId={bot.id} imgClassName="" />
                        : <BotIcon size={20} />}
                </div>
                <div className="adm-ident-text">
                    <div className="adm-title">
                        <span className="adm-name">{title}</span>
                        <BotStatus bot={bot} />
                    </div>
                    {bot.bot_username && bot.bot_name && <div className="adm-sub">{bot.bot_name}</div>}
                    <div className="adm-sub">
                        <button type="button" className="adm-owner" title="Ver todos os bots deste usuário" onClick={onOwner}>
                            <User size={12} /> {bot.owner_username}
                        </button>
                        <span className="adm-muted"> · {bot.owner_email}</span>
                    </div>
                </div>
            </div>

            <dl className="adm-metrics">
                <div>
                    <dt>Faturamento</dt>
                    <dd>{fmtBRL(bot.revenue)}</dd>
                </div>
                <div>
                    <dt>Vendas</dt>
                    <dd>{(bot.sales || 0).toLocaleString("pt-BR")}</dd>
                    <small>aprovadas</small>
                </div>
                <div>
                    <dt>Última atividade</dt>
                    <dd className="adm-dd-sm">{bot.last_activity ? fmtDateTimezone(bot.last_activity) : "—"}</dd>
                </div>
                <div>
                    <dt>Criado · Editado</dt>
                    <dd className="adm-dd-sm">
                        {fmtDateTimezone(bot.created_at, "date") || "—"} · {bot.updated_at ? fmtDateTimezone(bot.updated_at, "date") : "—"}
                    </dd>
                </div>
            </dl>

            <div className="adm-actions">
                <button type="button" className="adm-btn" title="Abrir a configuração deste bot" onClick={onEdit}>
                    <Pencil size={14} /> Editar
                </button>
                <button type="button" className="adm-btn" title="Ver e editar os disparos deste bot" onClick={onDisparos}>
                    <Send size={14} /> Disparos
                </button>
                <button type="button" className="adm-btn" title="Ver os pagamentos deste bot" onClick={onSales}>
                    <Receipt size={14} /> Vendas
                </button>
                {bot.is_blocked ? (
                    <button type="button" className="adm-btn" title="Desbloquear bot" onClick={onUnblock}>
                        <Unlock size={14} /> Desbloquear
                    </button>
                ) : (
                    <button type="button" className="adm-btn adm-btn--danger" title="Bloquear bot (para de responder na hora)" onClick={onBlock}>
                        <Ban size={14} /> Bloquear
                    </button>
                )}
            </div>

            <div className="adm-details">
                <BotCopyPreview text={bot.welcome_message} />
                {(plans.length > 0 || media.length > 0) && (
                    <div className="adm-details-side">
                        {plans.length > 0 && (
                            <div className="adm-tags">
                                {plans.map((p, i) => (
                                    <span key={`${bot.id}-plan-${i}`} className="adm-plan">
                                        <span>{p.name || `Plano ${i + 1}`}</span>
                                        {p.value != null && <b>{fmtBRL(p.value)}</b>}
                                        {p.duration && <em>{p.duration}</em>}
                                    </span>
                                ))}
                            </div>
                        )}
                        {media.length > 0 && (
                            <div className="adm-media">
                                {media.map((m, i) => <BotMediaThumb key={`${bot.id}-${i}`} media={m} botId={bot.id} />)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </article>
    );
}

// Filtros na URL. `owner` busca no nome/e-mail do dono; `owner_id` é exato
// (link de Usuários → Bots) e `owner_label` só dá nome ao chip.
const FILTER_KEYS = ["username", "owner", "owner_id", "owner_label", "blocked", "order"];

const SEARCHES = [
    { key: "username", placeholder: "@ do bot" },
    { key: "owner", placeholder: "Dono (nome ou e-mail)" },
];
const SELECTS = [
    { key: "blocked", label: "Status", options: [
        { value: "", label: "Todos os status" },
        { value: "true", label: "Somente bloqueados" },
    ] },
    { key: "order", label: "Ordem", options: [
        { value: "", label: "Maior faturamento" },
        { value: "recent", label: "Mais recentes" },
    ] },
];

export default function AdminBots() {
    const [bots, setBots] = useState([]);
    const [loading, setLoading] = useState(false);
    const { showError, showSuccess } = useNotification();
    const navigate = useNavigate();
    const [applied, applyFilters, filtersKey] = useUrlFilters(FILTER_KEYS);
    // order: "revenue" (maior faturamento primeiro, padrão) | "recent" (atividade)
    const order = applied.order || "revenue";
    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        loadBots(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey]);

    const update = (changes) => applyFilters({ ...applied, ...changes });

    const loadBots = async (page = 1) => {
        try {
            setLoading(true);
            const { data } = await dashboardService.getAdminBots({
                page,
                username: applied.username,
                owner: applied.owner,
                owner_id: applied.owner_id,
                blocked: applied.blocked,
                order,
            });
            setBots(data.bots || []);
            setPagination(data.pagination || {});
            setCurrentPage(page);
        } catch (err) {
            console.error(err);
            showError("Erro ao carregar bots.");
        } finally {
            setLoading(false);
        }
    };

    const handleBlock = async (bot) => {
        const ok = window.confirm(
            `Bloquear @${bot.bot_username || bot.id}?\n\nO bot para de responder imediatamente e o dono não consegue reativá-lo.`
        );
        if (!ok) return;

        try {
            await dashboardService.blockBot(bot.id);
            showSuccess(`Bot @${bot.bot_username || bot.id} bloqueado.`);
            loadBots(currentPage);
        } catch (err) {
            console.error(err);
            showError("Erro ao bloquear. " + (err?.response?.data?.error || err?.message || ""));
        }
    };

    const handleUnblock = async (bot) => {
        const ok = window.confirm(
            `Desbloquear @${bot.bot_username || bot.id}?\n\nO bot volta a funcionar imediatamente.`
        );
        if (!ok) return;

        try {
            const { data } = await dashboardService.unblockBot(bot.id);
            if (data.started) {
                showSuccess(`Bot @${bot.bot_username || bot.id} desbloqueado e iniciado.`);
            } else {
                showSuccess(data.message || `Bot desbloqueado, mas não foi possível iniciá-lo.`);
            }
            loadBots(currentPage);
        } catch (err) {
            console.error(err);
            showError("Erro ao desbloquear. " + (err?.response?.data?.error || err?.message || ""));
        }
    };

    return (
        <>
            <AdminFilters values={applied} onApply={applyFilters} searches={SEARCHES} selects={SELECTS}>
                {applied.owner_id && (
                    <FilterChip
                        label={`Bots de ${applied.owner_label || `#${applied.owner_id}`}`}
                        onClear={() => update({ owner_id: "", owner_label: "" })}
                    />
                )}
            </AdminFilters>

            {/* LISTA DE BOTS */}
            <div className="adm-list">
                {bots.map((bot) => (
                    <BotRow
                        key={bot.id}
                        bot={bot}
                        onOwner={() => update({
                            owner_id: bot.owner_id, owner_label: bot.owner_username, owner: "", username: "",
                        })}
                        onEdit={() => navigate(`/bots/manage/${bot.id}?tab=edit`)}
                        onDisparos={() => navigate(`/disparos?bot=${bot.id}`)}
                        onSales={() => {
                            const q = new URLSearchParams({
                                bot_id: bot.id,
                                bot_label: bot.bot_username ? `@${bot.bot_username}` : `Bot #${bot.id}`,
                            });
                            navigate(`/dashboard/admin/pagamentos?${q}`);
                        }}
                        onBlock={() => handleBlock(bot)}
                        onUnblock={() => handleUnblock(bot)}
                    />
                ))}
            </div>

            {bots.length === 0 && !loading && (
                <div className="empty-state">
                    <BotIcon size={28} />
                    <h4>Nenhum bot encontrado</h4>
                </div>
            )}

            {/* PAGINAÇÃO */}
            {!loading && bots.length > 0 && (
                <div className="mt-8 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Página {currentPage} de {pagination.pages} ({pagination.total} bots)
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => loadBots(currentPage - 1)}
                            disabled={!pagination.has_prev}
                            className="btn-secondary inline-flex items-center gap-1"
                        >
                            <ChevronLeft size={16} /> Anterior
                        </button>

                        <button
                            onClick={() => loadBots(currentPage + 1)}
                            disabled={!pagination.has_next}
                            className="btn-primary inline-flex items-center gap-1"
                        >
                            Próximo <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
